import React, { Fragment, useCallback, useContext } from 'react';
import { SxProp, styled, useSx } from 'dripsy';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { View, Animated } from 'react-native';
import { useNavigation, useRoute } from '@react-navigation/native';
import { useCollapsibleScene } from 'react-native-collapsible-tab-view';

import {
  ConversationList,
  Separator,
  BaseText,
  ScreenLoader,
} from '@components';
import { useEffectOnFocus } from '@hooks';
import { ToastService } from '@services';
import { parseApiError } from '@utils';
import { useChatsModel } from '@models/Chats/ChatsModel';
import {
  HomeTabNavigatorNavigationPropType,
  HomeTabNavigatorRoutePropType,
  screens,
} from '@navigation';
import type { Conversation } from '@models/Chats/ConversationModel';
import { AnimatedValueContext } from '@context';

const containerStyle: SxProp = {
  flex: 1,
};

const Title = styled(BaseText)({
  color: '$heading800',
  fontWeight: 'semi',
  variant: 'text.big',
});

const EmptyContainer = styled(View)({
  bg: '$background10',
  p: '$6',
  borderRadius: 12,
});

const EmptyTitle = styled(BaseText)({
  color: '$heading800',
  variant: 'text.xlarge',
  fontWeight: 'semi',
});

const EmptyDescription = styled(BaseText)({
  color: '$text400',
  variant: 'text.medium',
});

type NavigationPropType = HomeTabNavigatorNavigationPropType<'News'>;
type RoutePropType = HomeTabNavigatorRoutePropType<'News'>;

export const SideChatsList = observer(() => {
  const nav = useNavigation<NavigationPropType>();
  const { t } = useTranslation();
  const chats = useChatsModel();
  const route = useRoute<RoutePropType>();
  const { contentContainerStyle, tabBarHeight, progressViewOffset } =
    useCollapsibleScene(route.name);
  const scrollY = useContext(AnimatedValueContext);
  const sx = useSx();

  useEffectOnFocus(() => fetch());

  async function fetch() {
    try {
      await chats.list.fetch.run();
    } catch (error) {
      ToastService.showError(parseApiError(error).message);
    }
  }

  async function fetchMore() {
    try {
      await chats.list.fetchMore.run();
    } catch (error) {
      ToastService.showError(parseApiError(error).message);
    }
  }

  const navigateToConversation = useCallback((item: Conversation) => {
    nav.navigate(screens.ChatNavigator, {
      screen: screens.Chats,
      params: { conversationId: item.id },
    });
  }, []);

  const headerHeight = progressViewOffset - tabBarHeight;
  const translateY = scrollY
    ? scrollY.interpolate({
        inputRange: [0, Math.max(headerHeight, tabBarHeight)], // Always allow for a minimum of `tabBarHeight`
        outputRange: [0, -headerHeight],
        extrapolateRight: 'clamp',
      })
    : null;
  const paddingBottom = scrollY
    ? scrollY.interpolate({
        inputRange: [0, Math.max(headerHeight, tabBarHeight)], // Always allow for a minimum of `tabBarHeight`
        outputRange: [headerHeight, 0],
        extrapolateRight: 'clamp',
      })
    : null;

  if (chats.list.fetch.inProgress) {
    return <ScreenLoader />;
  }

  return (
    <Animated.View
      style={[
        sx(containerStyle),
        contentContainerStyle,
        translateY
          ? {
              transform: [{ translateY }],
            }
          : {},
        paddingBottom ? { paddingBottom } : {},
      ]}
    >
      <ConversationList
        ListHeaderComponent={() => (
          <Fragment>
            <Separator size="$9" />
            <Title>{t('home.chats')}</Title>
            <Separator size="$8" />
          </Fragment>
        )}
        data={chats.list.asArray}
        onRefresh={fetch}
        onEndReached={fetchMore}
        onPress={navigateToConversation}
        ListEmptyComponent={() => (
          <EmptyContainer>
            <EmptyTitle>{`${t('home.noFriends')} 🥺`}</EmptyTitle>
            <Separator size="$4" />
            <EmptyDescription>
              {t('home.findFriend')}
            </EmptyDescription>
          </EmptyContainer>
        )}
      />
    </Animated.View>
  );
});
